import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import ExcelJS from 'exceljs';

import { jsPDF } from "jspdf";
import "jspdf-autotable";

import { Card, CardContent } from "@mui/material";
import { Helmet } from "react-helmet-async";
import RetailerWiseProductWiseSchemeHeader from "../../RetailerProductWiseScheme/RetailerWiseProductWiseSchemeHeader2";
import RetailerWiseProductWiseSchemeService from "./RetailerWiseProductWiseClaimReportService";
import RetailerProductWiseClaimReportGrid from "./RetailerProductWiseClaimReportGrid";
import RetailerWiseProductWiseClaimFilterSearch from "./RetailerProductWiseClaimReportSearchFilter";
import LoadingProgressBar from "../../../components/loading-progressbar/loadingprogressbar";
import PopUpHandle from "../../../commons/PopUpHandle";
import { useAppStore } from "../../../store/appStore";


export default function RetailerProductWiseClaimReportMaster() {
    const location = useLocation();

    const getTitleFromPath = (pathname) => {
        const segments = pathname.split('/').filter(Boolean); // Remove empty segments
        const lastSegment = segments[segments.length - 1] || '';

        return lastSegment
            .replace(/-/g, ' ') // Replace hyphens with spaces
            .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize each word
    };

    const title = getTitleFromPath(location.pathname);

    const {
        moduleName,
        setModuleName,
        destroy,
        setSearchControls,
        searchControls,
        setResponseData,
        responseData,
        setFilteredData,
        filteredData,
        isListChanged,
        setIsListChanged,
        initialize,
        filterPayload
    } = useAppStore();

    useEffect(() => {
        if (moduleName !== title) {
            destroy(); //  this resets the entire store to initial state
            initialize();
            setResponseData({});
            setFilteredData(undefined);
            setModuleName(title);

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moduleName]);


    const [isFilterOpen, setFilterOpen] = useState(false); // Define state for filter modal
    const [loadingCount, setLoadingCount] = useState(0);
    const showLoading = () => setLoadingCount((prev) => prev + 1);
    const hideLoading = () => setLoadingCount((prev) => Math.max(prev - 1, 0));


    const [open, setOpen] = useState(false);
    const [error, setError] = useState('');
    const [errorCode, setErrorCode] = useState(0);



    const { pageType } = useParams();
    const handleOpenFilter = () => {
        setFilterOpen(true);
    };

    const handleCloseFilter = () => {
        setFilterOpen(false);
    };
    const handleClose = () => {
        setOpen(false);
    }

    useEffect(() => {
        const fetchDynamicUISearchControls = async () => {
            showLoading();
            try {
                const schemeConversionFilterServiceInstance = await RetailerWiseProductWiseSchemeService;

                if (!schemeConversionFilterServiceInstance) {
                    throw new Error("PostedFilterService instance could not be created.");
                }


                

                const response = await schemeConversionFilterServiceInstance.GetRetailerProductWiseClaimReportSearchControls();
                if (response && response.data) {
                    setSearchControls(response.data);
                } else {
                    console.error("Invalid response format for GetDynamicUISearchControls:", response);
                    setSearchControls([]);
                }
            } catch (err) {
                console.error("Error fetching Dynamic UI Search Controls:", err);
                setSearchControls([]);
            } finally {
                hideLoading();
            }
        };

        if (moduleName != null && (searchControls.length === 0 || isListChanged)) {
            fetchDynamicUISearchControls();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moduleName, isListChanged]);

    const downloadExcel = async () => {
        if (!filteredData || filteredData.length === 0) return;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Sheet1");

        // Derive columns dynamically from object keys
        const firstRow = filteredData[0];
        const columns = Object.keys(firstRow)
            .filter(key => key.toLowerCase() !== "id") // Skip ID column
            .map(key => ({
                header: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize header
                key,
            }));

        worksheet.columns = columns;

        // Add data rows with formatted dates
        filteredData.forEach(dataRow => {
            const row = {};
            Object.keys(dataRow).forEach(key => {
                if (key.toLowerCase().includes("date") && dataRow[key]) {
                    row[key] = new Date(dataRow[key]).toLocaleDateString("en-GB"); // DD/MM/YYYY
                } else {
                    row[key] = dataRow[key];
                }
            });
            worksheet.addRow(row);
        });

        // Auto-fit column width based on content
        worksheet.columns.forEach(column => {
            let maxLength = column.header.length;
            column.eachCell({ includeEmpty: true }, cell => {
                if (cell.value) {
                    const cellValue = cell.value.toString();
                    maxLength = Math.max(maxLength, cellValue.length);
                }
            });
            column.width = maxLength + 2;
        });

        // Style header row
        worksheet.getRow(1).eachCell(cell => {
            cell.font = { bold: true };
            cell.alignment = { vertical: "center", horizontal: "center" };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFC5D9F1" },
            };
        });

        // Style body rows
        worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
            if (rowNumber !== 1) {
                row.eachCell({ includeEmpty: true }, cell => {
                    cell.alignment = { vertical: "center", horizontal: "left", wrapText: true };
                    cell.border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" },
                    };
                });
            }
        });

        // Generate and download Excel
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        const timeStamp = new Date().toISOString().replace(/[:\-T]/g, "").slice(0, 15);
        link.download = `RetailerWiseProductWiseClaimReport_${timeStamp}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    const exportToPDF = () => {
        if (!filteredData || filteredData.length === 0) return;


        const allKeys = Object.keys(filteredData[0]).filter(
            (key) => key.toLowerCase() !== "id"
        );

        // Keep only columns that have at least one non-empty value
        const validKeys = allKeys.filter((key) =>
            filteredData.some(
                (row) =>
                    row[key] !== null &&
                    row[key] !== undefined &&
                    row[key].toString().trim() !== ""
            )
        );


        const headers = validKeys.map(
            (key) => key.charAt(0).toUpperCase() + key.slice(1)
        );


        const rows = filteredData.map((row) =>
            validKeys.map((key) => {
                const value = row[key];
                if (value === null || value === undefined) return "";
                if (key.toLowerCase().includes("date")) {
                    return new Date(value).toLocaleDateString("en-GB"); // DD/MM/YYYY
                }
                return value.toString();
            })
        );

        // eslint-disable-next-line new-cap
        const doc = new jsPDF({
            orientation: "landscape",
            unit: "pt",
            format: "a4",
        });

        // Optional title
        doc.text("Retailer Wise Product Wise Scheme", 40, 30);


        const columnWidths = validKeys.map((key) =>
            key.toLowerCase() === "name" ? 130 : "auto"
        );

        doc.autoTable({
            head: [headers],
            body: rows,
            styles: { cellPadding: 4, fontSize: 8 },
            headStyles: {
                fillColor: [197, 217, 241],
                textColor: 20,
                fontStyle: "bold",
                halign: "center",
                valign: "middle",
            },
            bodyStyles: {
                valign: "top",
            },
            columnStyles: Object.fromEntries(
                columnWidths.map((w, i) => [i, { cellWidth: w }])
            ),
            startY: 40,
        });


        const timeStamp = new Date().toISOString().replace(/[:\-T]/g, "").slice(0, 15);
        doc.save(`RetailerWiseProductWiseClaimReport_${timeStamp}.pdf`);
    };

    return (
        <Card sx={{
            padding: '2px',
            boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)',
            borderRadius: '10px',
            margin: '5px 3px 5px 3px',
            height: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,

        }}>
            <CardContent style={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                paddingBottom: '0.5rem',
                minHeight: 0
            }}>
                <Helmet>
                    <title>{`${title} | ProClaimz`}</title>
                </Helmet>
                <RetailerWiseProductWiseSchemeHeader headerName={title} exportToPDF={exportToPDF} exportToExcel={downloadExcel} handleOpenFilter={handleOpenFilter} />
                <RetailerWiseProductWiseClaimFilterSearch open={isFilterOpen} onClose={handleCloseFilter} />
                <RetailerProductWiseClaimReportGrid headerName={title} />
                {loadingCount > 0 && <LoadingProgressBar />}
                {open && <PopUpHandle open={open} handleClose={handleClose} error={error} errorCode={errorCode} />}

            </CardContent>
        </Card>
    )
}